import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Tooltip } from 'bootstrap/dist/js/bootstrap.esm';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { PATH, PATTERNS } from '@app/constants';
import { AuthService, CampaignAnalyticsService, CookieService, UserService } from '@app/services';
import { TextValidators } from '@app/services/validators/password.validator';
import { MustMatchForm2 } from '@app/helpers/must-match-form.validator';
import { UserValidators } from '@app/classes/user-validators.class';
import { Subscription, of } from 'rxjs';
import { AnalyticsService } from '@app/services/analytics/analytics.service';
import { SingularService } from 'src/infra/analytics/singular/singular.service';
import { DivVisibilityService } from '@app/services/div-visibility/div-visibility.service';

@Component({
   templateUrl: './authentication-register.component.html',
   styleUrls: ['./authentication-register.component.scss']
})
export class AuthenticationRegisterComponent implements OnInit, AfterViewInit, OnDestroy {
   registerForm: FormGroup;

   passwordBlur = false;
   passwordFlag = 'password';
   passwordFlag2 = 'password';

   isDevelopment = false;

   showReferred = false;
   showPromotion = false;
   private campaignFragment: string;

   referredPatern = { 'X': { pattern: new RegExp('[a-zA-Z\\d]'), optional: true } };

   promotionsPatern = { 'X': { pattern: new RegExp('[a-zA-Z\\d]'), optional: true } };

   patternEmail = { 'E': { pattern: new RegExp('[a-zA-Z\\d@._-]'), optional: true } };

   email$: Subscription;

   constructor(
      private fb: FormBuilder,
      private authServ: AuthService,
      private userServ: UserService,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private analyticsService: AnalyticsService,
      private cookiesService: CookieService,
      private singularService: SingularService,
      private divVisibilityService: DivVisibilityService, 
      private analyticsCampaServ: CampaignAnalyticsService
   ) { }

   get email() {
      return this.registerForm.controls['email'];
   }

   get phone() {
      return this.registerForm.controls['phone'];
   }

   get userType() {
      return this.registerForm.controls['type'].value;
   }

     // Métodos para mostrar y ocultar el banners de registro

   showDiv() {
      this.divVisibilityService.showDiv();
    }
  
    hideDiv() {
      this.divVisibilityService.hideDiv();
    }

   // Función para crear la instancia de FormControl para el campo de contraseña
   createPasswordFormControl(passwordValue: string, email: string): FormControl {
      return new FormControl(passwordValue, [
         Validators.required,
         TextValidators.password,
         (control: AbstractControl) => UserValidators.passwordEmailMatchValidator(email, { emailViolation: true })(control)
      ]);
   }

   onEmailChange(emailValue: string) {
      const passwordControl = this.registerForm.get('password');
      const emailValueWithoutDomain = emailValue.split('@')[0];
      if (passwordControl) {
         const currentPasswordValue = passwordControl.value;
         this.registerForm.setControl('password', this.createPasswordFormControl(currentPasswordValue, emailValueWithoutDomain));
      }
   }

   ngOnInit(): void {
      const referred = sessionStorage.getItem(this.authServ.REFERENCE_USER);
      const promoOrAnalytics = sessionStorage.getItem(this.authServ.ANALYTIC_CODE);
      this.showReferred = !!referred;
      this.showPromotion = !!promoOrAnalytics;

      this.registerForm = this.fb.group({
         type: new FormControl('u'),
         terms: new FormControl(false, [Validators.requiredTrue]),
         mailProm: new FormControl(true),
         email: new FormControl('', [Validators.compose([
            Validators.required,
            UserValidators.patternValidator(PATTERNS.email, { validEmail: false })
         ])]),
         phone: new FormControl('', [Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(15)])]),
         password: new FormControl('', [Validators.compose([
            Validators.required,
            TextValidators.password,
         ])]),
         confirmPassword: new FormControl(''),
         referred: new FormControl({ value: referred || '', disabled: !!referred }, [Validators.minLength(3)]),
         promo: new FormControl({ value: promoOrAnalytics || '', disabled: !!promoOrAnalytics }, [Validators.minLength(3)]),
      }, {
         // updateOn: 'blur',
         validators: [
            MustMatchForm2('email', 'email', 'notMatchEmail'),
            MustMatchForm2('password', 'confirmPassword', 'notMatchPass')
         ]
      });

      this.email$ = this.registerForm.controls.email.valueChanges.subscribe((email: string) => {
         this.onEmailChange(email);
      });

      /** Función para obtener por la URL si el tipo de usuario es solicitante o inversionista */
      this.activatedRoute.queryParams.subscribe(params => {
         if (params.d) {
            this.registerForm.controls.type.setValue('d');
         }
      });

      this.campaignFragment = this.getCampaignFragment();
   }

   private getCampaignFragment(): string {
      return this.cookiesService.get('landing_campaign_fragment') || null;
   }

   ngAfterViewInit(): void {
      Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]')).forEach(tooltipNode => new Tooltip(tooltipNode));
   }

   changeUserType(type: 'u' | 'd') {
      this.registerForm.controls['type'].setValue(type);
      // Push datalayer event for user type change (solicitante / inversionista) in register form
      this.analyticsService.pushEvent('userTypeChange', { userType: type === 'u' ? 'solicitante' : 'inversionista'});
   }

   register() {
      if (this.registerForm.valid) {
         const { email, password, type, terms, mailProm, referred, promo, phone } = this.registerForm.getRawValue();


         const ref = this.showReferred ? referred : this.authServ.referred || undefined;
         const prom = this.showPromotion ? promo : this.authServ.registerPromo || undefined;
         const cf = this.campaignFragment || undefined;

         this.authServ.registry(email, password, type, terms, mailProm, ref, prom, phone, cf)
            .pipe(
               switchMap(() => this.userServ.getUserInf()),
               tap(() => {
                  this.analyticsService.pushEvent('basic_register', { status: 'success', userType: type === 'u' ? 'inversionista' : 'solicitante' });
                  this.singularService.sendEvent('__REGISTRO_SIMPLE__', { userType: type === 'u' ? 'inversionista' : 'solicitante' });
               }),
               catchError(() => {
                  this.analyticsService.pushEvent('basic_register', { status: 'error', userType: type === 'u' ? 'inversionista' : 'solicitante' });
                  return of(null);
               })
            )
            .subscribe(u => {
               this.saveAnalytics();
               if (u === null) {
                  return;
               }
               if (u.type === 'd') {
                  this.router.navigate([PATH.ROUTE_DEVELOPER]);
               } else {
                  this.router.navigate([PATH.ROUTE_USER_ONBOARDING]);
               }
            });
      }
   }


   /** Función para guardar los datos de analytics */
   saveAnalytics() {
      let source = this.analyticsCampaServ.getStoredParams();
      if(source?.source) {
         if (Object.keys(source).length >= 1) {
            source.fromUrl = window.location.pathname;
            
            // Guardar los datos de analytics en la base de datos
            this.userServ.saveAnalytics(source).subscribe(res => {
              if(res) {
                localStorage.removeItem('source');
              }
            });
          }
      }
   }

   @Output() toggle = new EventEmitter<void>();

   toggleDiv() {
     this.toggle.emit();
   }

   goToWarning() {
      window.open('https://monific.com/legal/aviso-de-privacidad', '_blank');
   }

   goToTerms() {
      window.open('https://monific.com/legal/terminos-y-condiciones', '_blank');
   }

   goTo(url: string) {
      window.open(url, '_blank', 'noopener');
   }

   ngOnDestroy(): void {
      this.email$.unsubscribe();
   }

}
